import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, X, Building2, User } from "lucide-react";
import { listCustomers, upsertCustomer, deleteCustomer, listRentals, MIGRATION_MISSING } from "../../../lib/adminApi";
import { MigrationNotice } from "../../../components/admin/MigrationNotice";

const inputCls =
  "w-full bg-[#FAFBF6] border border-[#B09060]/25 rounded-sm px-3 py-2.5 text-sm text-[#404040] focus:outline-none focus:border-[#B09060]";
const labelCls = "block text-xs uppercase tracking-[0.15em] font-semibold text-[#8F7648] mb-1";
const EMPTY = { type: "kurumsal", name: "", contact_name: "", phone: "", email: "", tax_no: "", address: "", notes: "" };

export default function CustomersSection() {
  const [customers, setCustomers] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [state, setState] = useState("loading");
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([listCustomers(), listRentals()]);
      setCustomers(c);
      setRentals(r);
      setState("ready");
    } catch (e) {
      setState(e === MIGRATION_MISSING ? "missing" : "ready");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const row = {
        type: editing.type,
        name: editing.name.trim(),
        contact_name: editing.contact_name || null,
        phone: editing.phone || null,
        email: editing.email || null,
        tax_no: editing.tax_no || null,
        address: editing.address || null,
        notes: editing.notes || null,
      };
      await upsertCustomer(editing.id ? { ...row, id: editing.id } : row);
      toast.success(editing.id ? "Müşteri güncellendi." : "Müşteri eklendi.");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.message || "Kaydedilemedi.");
    }
    setSaving(false);
  }

  async function remove(c) {
    if (!window.confirm(`"${c.name}" silinsin mi? (Kiralaması varsa veritabanı engeller)`)) return;
    try {
      await deleteCustomer(c.id);
      toast.success("Müşteri silindi.");
      setCustomers((cs) => cs.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(err.message || "Silinemedi.");
    }
  }

  if (state === "missing") return <MigrationNotice section="Müşteri Yönetimi" />;
  if (state === "loading") return <div className="text-[#6B6B6B] text-sm py-10" data-testid="customers-loading">Yükleniyor...</div>;

  return (
    <div data-testid="customers-section">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h2 className="font-serif-display text-2xl text-[#404040]">Müşteri Yönetimi</h2>
          <p className="text-sm text-[#6B6B6B] mt-1">{customers.length} kayıt</p>
        </div>
        <button
          onClick={() => setEditing({ ...EMPTY })}
          data-testid="customer-add-button"
          className="flex items-center gap-2 px-5 py-2.5 rounded-sm bg-[#B09060] text-white text-xs font-semibold uppercase tracking-[0.12em] hover:bg-[#8F7648] transition-colors"
        >
          <Plus className="w-4 h-4" /> Yeni Müşteri
        </button>
      </div>

      {customers.length === 0 ? (
        <div className="text-center py-16 text-[#6B6B6B]" data-testid="customers-empty">
          <p className="text-sm">Henüz müşteri kaydı yok.</p>
        </div>
      ) : (
        <div className="space-y-3 mt-6">
          {customers.map((c) => {
            const history = rentals.filter((r) => r.customer_id === c.id);
            const open = expanded === c.id;
            return (
              <div key={c.id} data-testid={`customer-row-${c.id}`} className="bg-white border border-[#B09060]/20 rounded-sm shadow-[0_4px_20px_rgba(176,144,96,0.05)]">
                <button
                  className="w-full text-left p-5 flex flex-wrap items-center gap-3"
                  onClick={() => setExpanded(open ? null : c.id)}
                  data-testid={`customer-expand-${c.id}`}
                >
                  {c.type === "kurumsal"
                    ? <Building2 className="w-5 h-5 text-[#B09060] shrink-0" strokeWidth={1.25} />
                    : <User className="w-5 h-5 text-[#B09060] shrink-0" strokeWidth={1.25} />}
                  <div className="flex-1 min-w-48">
                    <div className="font-semibold text-[#404040] text-sm">{c.name}</div>
                    <div className="text-xs text-[#6B6B6B] mt-0.5">
                      {c.contact_name ? `${c.contact_name} · ` : ""}{c.phone || "—"} {c.email ? `· ${c.email}` : ""}
                    </div>
                  </div>
                  <span className="px-2.5 py-0.5 rounded-full bg-[#F2F3EB] text-[#6B6B6B] text-[10px] uppercase tracking-wider">{c.type}</span>
                  <span className="text-[11px] text-[#9B9B8F]">{history.length} kiralama</span>
                </button>

                {open && (
                  <div className="px-5 pb-5 border-t border-[#B09060]/10 pt-4 space-y-3" data-testid={`customer-detail-${c.id}`}>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div><span className="text-[#9B9B8F] block">Vergi No</span><span className="text-[#404040]">{c.tax_no || "—"}</span></div>
                      <div className="col-span-2"><span className="text-[#9B9B8F] block">Adres</span><span className="text-[#404040]">{c.address || "—"}</span></div>
                      <div><span className="text-[#9B9B8F] block">Kayıt</span><span className="text-[#404040]">{new Date(c.created_at).toLocaleDateString("tr-TR")}</span></div>
                    </div>
                    {c.notes && <p className="text-xs text-[#6B6B6B]">{c.notes}</p>}
                    {history.length > 0 && (
                      <div>
                        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8F7648] mb-2">Kiralama Geçmişi</div>
                        <div className="space-y-1.5">
                          {history.map((r) => (
                            <div key={r.id} className="text-xs text-[#404040] bg-[#FAFBF6] border border-[#B09060]/15 rounded-sm px-3 py-2">
                              {r.vehicles?.name} · {new Date(r.start_date).toLocaleDateString("tr-TR")} – {new Date(r.end_date).toLocaleDateString("tr-TR")} · {new Intl.NumberFormat("tr-TR").format(r.monthly_rate)} TL/ay · {r.status}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex gap-2 pt-1">
                      <button
                        onClick={() => setEditing({ ...EMPTY, ...c })}
                        data-testid={`customer-edit-${c.id}`}
                        className="px-3.5 py-1.5 rounded-sm border border-[#B09060]/30 text-[#8F7648] text-xs font-semibold uppercase tracking-wider hover:bg-[#F2F3EB] transition-colors"
                      >
                        Düzenle
                      </button>
                      <button
                        onClick={() => remove(c)}
                        data-testid={`customer-delete-${c.id}`}
                        className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-sm border border-red-200 text-red-600 text-xs font-semibold uppercase tracking-wider hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Sil
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="customer-edit-dialog">
          <form onSubmit={save} className="w-full max-w-lg bg-white border border-[#B09060]/20 rounded-sm p-7 space-y-4 shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-xl text-[#404040]">{editing.id ? "Müşteriyi Düzenle" : "Yeni Müşteri"}</h3>
              <button type="button" onClick={() => setEditing(null)} data-testid="customer-dialog-close" className="text-[#9B9B8F] hover:text-[#404040]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Tür</label>
                <select data-testid="customer-form-type" className={inputCls} value={editing.type}
                  onChange={(e) => setEditing({ ...editing, type: e.target.value })}>
                  <option value="kurumsal">Kurumsal</option>
                  <option value="bireysel">Bireysel</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>{editing.type === "kurumsal" ? "Firma Ünvanı *" : "Ad Soyad *"}</label>
                <input required data-testid="customer-form-name" className={inputCls} value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Yetkili Kişi</label>
                <input className={inputCls} value={editing.contact_name || ""}
                  onChange={(e) => setEditing({ ...editing, contact_name: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Telefon</label>
                <input className={inputCls} value={editing.phone || ""}
                  onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>E-posta</label>
                <input type="email" className={inputCls} value={editing.email || ""}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              </div>
              <div>
                <label className={labelCls}>Vergi No</label>
                <input className={inputCls} value={editing.tax_no || ""}
                  onChange={(e) => setEditing({ ...editing, tax_no: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Adres</label>
                <input className={inputCls} value={editing.address || ""}
                  onChange={(e) => setEditing({ ...editing, address: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Notlar</label>
                <textarea rows={2} className={inputCls} value={editing.notes || ""}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button
                type="submit"
                disabled={saving}
                data-testid="customer-form-save"
                className="flex-1 py-3 rounded-sm bg-[#B09060] text-white font-semibold text-xs uppercase tracking-[0.12em] hover:bg-[#8F7648] transition-colors disabled:opacity-50"
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </button>
              <button type="button" onClick={() => setEditing(null)}
                className="px-6 py-3 rounded-sm border border-[#B09060]/30 text-[#404040] text-xs font-semibold uppercase tracking-wider hover:bg-[#F2F3EB] transition-colors">
                Vazgeç
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
